import React, { useCallback, useState, useEffect } from "react";
import {
  GoogleMap,
  LoadScript,
  Marker,
  MarkerClusterer,
} from "@react-google-maps/api";
import { IDataSource } from "../../api/models";
import pinOn from "../../assets/images/pin-on.png";
import pinOff from "../../assets/images/pin-off.png";

const LocationsMap: React.FC<{
  options: IDataSource[];
  selected: IDataSource[];
  handleSelect: (sourceId: string) => void;
}> = ({ options, selected, handleSelect }) => {
  const [center, setCenter] = useState<{ lat: number; lng: number } | null>(
    null
  );
  const [mapError, setMapError] = useState<string | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCenter({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.error("Error getting user location:", error);
          // Fallback to centering map based on options if geolocation fails
          if (options.length > 0) {
            const latSum = options.reduce(
              (sum, option) => sum + option.latitude,
              0
            );
            const lngSum = options.reduce(
              (sum, option) => sum + option.longitude,
              0
            );
            setCenter({
              lat: latSum / options.length,
              lng: lngSum / options.length,
            });
          }
        }
      );
    } else {
      // Fallback if geolocation is not supported
      if (options.length > 0) {
        const latSum = options.reduce(
          (sum, option) => sum + option.latitude,
          0
        );
        const lngSum = options.reduce(
          (sum, option) => sum + option.longitude,
          0
        );
        setCenter({
          lat: latSum / options.length,
          lng: lngSum / options.length,
        });
      }
    }
  }, []);

  const handleMapLoad = useCallback(
    (mapInstance: google.maps.Map) => {
      setMap(mapInstance);
      if (center) {
        mapInstance.setCenter(center);
      }
    },
    [center]
  );

  useEffect(() => {
    if (map && center) {
      map.setCenter(center);
    }
  }, [map, center]);

  if (mapError) {
    return <div>Error loading map: {mapError}</div>;
  }

  if (!center) {
    return <div>Loading map...</div>;
  }

  const isSelected = (option: IDataSource) => {
    console.log({ options, selected });
    return selected.some(
      (selectedOption) => `${selectedOption.sourceId}` === `${option.sourceId}`
    );
  };

  const handleMarkerClick = (sourceId: string) => {
    handleSelect(sourceId);
    // No map centering or other side effects should happen here
  };

  return (
    <LoadScript
      googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? ""}
      onError={() => setMapError("Failed to load Google Maps API.")}
    >
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "400px" }}
        zoom={10}
        center={center}
        onLoad={handleMapLoad}
      >
        <MarkerClusterer minimumClusterSize={10}>
          {(clusterer) => (
            <>
              {options.map((option) => (
                <Marker
                  title={option.title}
                  key={option.sourceId}
                  onClick={() => {
                    handleMarkerClick(option.sourceId);
                  }}
                  position={{
                    lat: Number(option.latitude),
                    lng: Number(option.longitude),
                  }}
                  clusterer={clusterer}
                  icon={{
                    url: isSelected(option) ? pinOn : pinOff,
                    scaledSize: new google.maps.Size(30, 30),
                  }}
                  zIndex={isSelected(option) ? 1 : 0}
                />
              ))}
            </>
          )}
        </MarkerClusterer>
      </GoogleMap>
    </LoadScript>
  );
};

export default LocationsMap;
